import { FC, ReactNode, useEffect } from "react";

import {
  useHookForm,
  useMerchantApiMutation,
  useNotifications,
} from "../../../../hooks";
import {
  IAcceptAsPartialPayment,
  IHookForm,
  IViewTransaction,
  IViewVirtualAccountTransferRefund,
} from "../../../../interfaces-and-types";
import { globalHandlerUtils } from "../../../../utils";

import { View } from "./View";
import { acceptAsPartialSchema } from "./validationSchema";
import { chargeBearer } from "../../../../enums";

export interface IAcceptAsPartialPaymentFormViewProps
  extends IHookForm<IAcceptAsPartialPayment> {
  // current state of loading view
  isLoadingFormData: boolean;
  isFormEnabled: boolean;
  formDisabledReason: ReactNode | null;
  refundAmount: number;
  refundCurrency: string;
  destinationTransactionOrParent: IViewTransaction | null;
}

export interface IAcceptAsPartialPaymentFormProps {
  // current state of loading view
  isLoadingFormData: boolean;
  isFormEnabled: boolean;
  formDisabledReason: ReactNode | null;
  // refund details
  refundAmount: number;
  refundCurrency: string;
  destinationTransactionOrParent: IViewTransaction | null;
  // form valuess
  transferRefundId: string;
  transactionId: string;
  sourceTxBearer: chargeBearer;
}

export const AcceptAsPartialPaymentForm: FC<
  IAcceptAsPartialPaymentFormProps
> = (props) => {
  const notificationsHandler = useNotifications();

  const {
    isLoading: isLoadingAcceptAsPartial,
    isSuccess: isSuccessAcceptAsPartial,
    // isError: isErrorAcceptAsPartial,
    data: acceptAsPartialApiResponse,
    mutate: acceptAsPartialPayment,
  } = useMerchantApiMutation<
    IAcceptAsPartialPayment,
    IViewVirtualAccountTransferRefund
  >(
    "acceptPaymentIssueAsPartialPayment",
    ["getVirtualAccountTransferRefundById"],
    {
      // ignoreActivityBlocker: true,
    },
    (urlTemplate) => urlTemplate.replace("id", props.transferRefundId)
  ).usePostMutation;

  const initialFormValues: IAcceptAsPartialPayment = {
    transfer_refund_id: props.transferRefundId,
    transaction_id: props.transactionId,
    max_partial_payment_count: 5,
    bearer: props.sourceTxBearer,
    description: "",
  };

  const handleApiSubmit = (data: IAcceptAsPartialPayment) => {
    acceptAsPartialPayment(data);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const { onFormSubmit, errors, register, reset } = useHookForm({
    initialFormValues,
    validationSchema: acceptAsPartialSchema,
    onValid: handleApiSubmit,
    onInvalid: handleError,
  });

  errors && console.warn("AcceptAsPartialPaymentForm.errors: ", errors);

  useEffect(() => {
    reset(initialFormValues);
  }, [
    props.transferRefundId,
    props.transactionId,
    props.sourceTxBearer,
    props.destinationTransactionOrParent?.id,
  ]);

  useEffect(() => {
    if (isSuccessAcceptAsPartial) {
      reset();

      // display a notification of success
      notificationsHandler.showNotification({
        ariaLabel: "info notification",
        title: "Payment Issue Settled Successfully",
        kind: "info",
        message:
          "The payment issue has been resolved. It has been accepted as a partial payment.",
        statusIconDescription: "notification",
        timeout: 10000,
      });
    }
  }, [isSuccessAcceptAsPartial]);

  return (
    <View
      {...props}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
    />
  );
};
